// src/components/Header.jsx
// Nuvport OS v2.0 — 6-group dropdown nav · Signal Green · neutral charcoal
import { useState, useEffect, useRef } from "react";
import WorldClock from "./WorldClock";

// ── 5-group navigation architecture ─────────────────────────────────────────
// pro: true → requires Pro tier. Free users see lock icon + upgrade prompt.
const NAV_GROUPS = [
  {
    id: "portfolio",
    label: "PORTFOLIO",
    key: "P",
    tabs: [
      { id: "dashboard",  label: "DASHBOARD",  code: "HOME"  },
      { id: "builder",    label: "BUILDER",    code: "BUILD" },
      { id: "valuation",  label: "VALUATION",  code: "VAL",  pro: true },
      { id: "rv",         label: "REL. VALUE", code: "RV",   pro: true },
      { id: "optimizer",  label: "OPTIMIZER",  code: "OPT",  pro: true },
    ],
  },
  {
    id: "simulation",
    label: "SIMULATION",
    key: "S",
    tabs: [
      { id: "backtest",   label: "BACKTEST",    code: "BT",  pro: true },
      { id: "dcf",        label: "DCF MODEL",   code: "DCF", pro: true },
      { id: "montecarlo", label: "MONTE CARLO", code: "MC",  pro: true },
      { id: "charts",     label: "ANALYTICS",   code: "ANA", pro: true },
    ],
  },
  {
    id: "markets",
    label: "MARKETS",
    key: "M",
    tabs: [
      { id: "marketwatch", label: "WATCHLIST",    code: "WATCH" },
      { id: "screener",    label: "SCREENER",     code: "SCR",  pro: true },
      { id: "etfscreener", label: "ETF SCREENER", code: "ETF",  pro: true },
      { id: "fxmarkets",   label: "FX & MARKETS", code: "FX",   pro: true },
      { id: "futures",     label: "FUTURES",      code: "FUT",  pro: true },
      { id: "fixedincome", label: "FIXED INCOME", code: "FI",   pro: true },
    ],
  },
  {
    id: "macro",
    label: "MACRO",
    key: "K",
    tabs: [
      { id: "macro", label: "MACRO DASHBOARD",  code: "MAC",  pro: true },
      { id: "fed",   label: "FED MONITOR",      code: "FED",  pro: true },
      { id: "ecst",  label: "ECON STATISTICS",  code: "ECST", pro: true },
      { id: "wei",   label: "WORLD ECON INDEX", code: "WEI",  pro: true },
    ],
  },
  {
    id: "global",
    label: "GLOBAL",
    key: "G",
    tabs: [
      { id: "world", label: "GEO-RISK MAP",     code: "GEO",  pro: true },
      { id: "wcrs",  label: "WORLD CURRENCIES", code: "WCRS", pro: true },
    ],
  },
  {
    id: "intel",
    label: "INTELLIGENCE",
    key: "I",
    tabs: [
      { id: "news",    label: "NEWS SENTIMENT", code: "NEWS", pro: true },
      { id: "cacs",    label: "EARNINGS",       code: "EARN", pro: true },
      { id: "insider", label: "INSIDER INTEL",  code: "II",   pro: true },
    ],
  },
];

// Flat list of all tabs for CMD+K search
const ALL_TABS = NAV_GROUPS.flatMap(g =>
  g.tabs.map(t => ({ ...t, group: g.label, groupId: g.id }))
);

export default function Header({
  metrics, totalWeight, activeTab, setActiveTab,
  onOpenSettings, onOpenBilling, onToggleWorkspace, displayName, user, onSignOut, tier, isAdmin,
}) {
  const [drawerOpen,   setDrawerOpen]  = useState(false);
  const [cmdkOpen,     setCmdkOpen]    = useState(false);
  const [cmdkQuery,    setCmdkQuery]   = useState("");
  const [cmdkIdx,      setCmdkIdx]     = useState(0);
  const [openGroup,    setOpenGroup]   = useState(null);
  const [upgradeOpen,  setUpgradeOpen] = useState(false);
  const cmdkInputRef = useRef(null);
  const hoverTimeout = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const isFree = !tier || tier === "free";

  // Gate pro tabs for free users
  const handleTabSelect = (tabId) => {
    const tab = ALL_TABS.find(t => t.id === tabId);
    if (tab?.pro && isFree) { setUpgradeOpen(true); return; }
    setActiveTab(tabId);
  };

  useEffect(() => {
    const handler = (e) => {
      if (!dropdownRef.current?.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // CMD+K keyboard shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setCmdkOpen(v => !v);
        setCmdkQuery("");
        setCmdkIdx(0);
      }
      if (e.key === "Escape") {
        setCmdkOpen(false);
        setOpenGroup(null);
      }
      // Group keyboard shortcuts (H/P/M/K/G/I) when CMD+K not open
      if (!cmdkOpen && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const group = NAV_GROUPS.find(g => g.key === e.key.toUpperCase());
        if (group && document.activeElement.tagName !== "INPUT" && document.activeElement.tagName !== "TEXTAREA") {
          const firstTab = group.tabs[0];
          if (firstTab) setActiveTab(firstTab.id);
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [cmdkOpen, setActiveTab]);

  // Focus CMD+K input when opened
  useEffect(() => {
    if (cmdkOpen) {
      setTimeout(() => cmdkInputRef.current?.focus(), 50);
    }
  }, [cmdkOpen]);

  // CMD+K results
  const cmdkResults = cmdkQuery.trim()
    ? ALL_TABS.filter(t =>
        t.label.toLowerCase().includes(cmdkQuery.toLowerCase()) ||
        t.code.toLowerCase().includes(cmdkQuery.toLowerCase()) ||
        t.group.toLowerCase().includes(cmdkQuery.toLowerCase())
      )
    : ALL_TABS;

  const handleCmdkKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setCmdkIdx(i => Math.min(i + 1, cmdkResults.length - 1)); }
    if (e.key === "ArrowUp")   { e.preventDefault(); setCmdkIdx(i => Math.max(i - 1, 0)); }
    if (e.key === "Enter") {
      const tab = cmdkResults[cmdkIdx];
      if (tab) { handleTabSelect(tab.id); setCmdkOpen(false); }
    }
  };

  const weightOk   = Math.abs(totalWeight - 100) < 0.1;
  const weightOver = totalWeight > 100.05;
  const weightColor = weightOk ? "var(--positive)" : weightOver ? "var(--negative)" : "var(--caution)";

  function getInitials(dName, email) {
    if (dName?.trim()) {
      return dName.trim().split(/\s+/).map(w => w[0]).join("").slice(0, 2).toUpperCase();
    }
    if (email) return email.split("@")[0].slice(0, 2).toUpperCase();
    return "?";
  }
  const initials = getInitials(displayName, user?.email);

  // Find active group for highlight
  const activeGroupId = NAV_GROUPS.find(g => g.tabs.some(t => t.id === activeTab))?.id;

  const handleGroupEnter = (groupId) => {
    clearTimeout(hoverTimeout.current);
    setOpenGroup(groupId);
  };
  const handleGroupLeave = () => {
    hoverTimeout.current = setTimeout(() => setOpenGroup(null), 120);
  };

  return (
    <>
      <style>{`
        @keyframes blink-hdr { 0%,100%{opacity:1} 50%{opacity:.12} }
        .hdr-dot { animation: blink-hdr 2.8s ease infinite; }
        .hdr-btn { transition: all .12s; }
        .hdr-btn:hover {
          border-color: var(--accent-mid) !important;
          color: var(--accent) !important;
          background: var(--accent-dim) !important;
        }
        .hdr-signout { transition: color .12s; }
        .hdr-signout:hover { color: var(--negative) !important; }
        .hdr-upgrade { transition: all .15s; }
        .hdr-upgrade:hover { box-shadow: 0 0 20px rgba(61,220,132,.4) !important; transform: translateY(-1px); }
        .hdr-kpi { transition: transform .12s; }
        .hdr-kpi:hover { transform: translateY(-1px); }
        .nav-group-btn { transition: all .12s; cursor: pointer; }
        .nav-group-btn:hover, .nav-group-btn.active {
          color: var(--accent) !important;
          background: var(--accent-dim) !important;
        }
        .nav-dropdown-item { transition: background .1s; cursor: pointer; }
        .nav-dropdown-item:hover, .nav-dropdown-item.active {
          background: var(--accent-dim);
        }
        .nav-dropdown-item.active { color: var(--accent) !important; }
        .cmdk-item:hover, .cmdk-item.active { background: var(--accent-dim); }
      `}</style>

      {/* Signal Green accent line — top edge */}
      <div style={{
        height: 1,
        background: "linear-gradient(90deg, transparent 0%, rgba(61,220,132,0.3) 20%, rgba(61,220,132,0.6) 50%, rgba(61,220,132,0.3) 80%, transparent 100%)",
        position: "sticky",
        top: 0,
        zIndex: 101,
      }} />

      <header style={{
        background: "var(--surface)",
        position: "sticky",
        top: 1,
        zIndex: 100,
        borderBottom: "1px solid var(--border)",
      }}>

        {/* ── Top bar ──────────────────────────────────────────── */}
        <div style={{ padding: "0 24px", borderBottom: "1px solid var(--border)" }}>
          <div style={{
            maxWidth: 1320, margin: "0 auto",
            display: "flex", alignItems: "center",
            gap: 16, height: 48,
          }}>

            {/* Logo */}
            <div
              onClick={() => setActiveTab("dashboard")}
              role="button"
              aria-label="Go to Dashboard"
              style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0, cursor: "pointer" }}
            >
              <svg width="32" height="32" viewBox="0 0 52 52" fill="none" style={{ flexShrink: 0 }}>
                <rect width="52" height="52" rx="13" fill="#0d1520"/>
                <rect x="12" y="10" width="6" height="32" rx="3" fill="#3ddc84"/>
                <rect x="34" y="10" width="6" height="32" rx="3" fill="#3ddc84" opacity="0.28"/>
                <line x1="15" y1="11.5" x2="37" y2="40.5" stroke="#3ddc84" strokeWidth="6" strokeLinecap="round"/>
              </svg>
              <div style={{ display: "flex", flexDirection: "column", gap: 1, userSelect: "none" }}>
                <span style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontWeight: 700,
                  fontSize: 18,
                  letterSpacing: "0.08em",
                  color: "#ffffff",
                  lineHeight: 1,
                }}>
                  NUVPORT
                </span>
                <span style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 8,
                  letterSpacing: "0.3em",
                  color: "var(--accent)",
                  lineHeight: 1,
                }}>
                  OS
                </span>
              </div>
              <span style={{
                color: "var(--text-tertiary)",
                fontFamily: "var(--font-mono)",
                fontSize: 8,
                letterSpacing: "0.1em",
                background: "var(--surface-3)",
                padding: "2px 6px",
                borderRadius: "var(--radius-sm)",
                border: "1px solid var(--border)",
                flexShrink: 0,
              }}>
                v3.0.0
              </span>
            </div>

            {/* World clock */}
            <div style={{ marginLeft: 4, flexShrink: 0 }}>
              <WorldClock compact />
            </div>

            <div style={{ flex: 1 }} />

            {/* CMD+K hint */}
            <button
              onClick={() => { setCmdkOpen(true); setCmdkQuery(""); setCmdkIdx(0); }}
              className="hdr-btn"
              style={{
                background: "transparent",
                border: "1px solid var(--border)",
                color: "var(--text-tertiary)",
                cursor: "pointer",
                padding: "4px 10px",
                fontFamily: "var(--font-mono)",
                fontSize: 9,
                letterSpacing: ".08em",
                borderRadius: "var(--radius-sm)",
                flexShrink: 0,
                display: "flex", alignItems: "center", gap: 6,
              }}
            >
              <span>⌘K</span>
              <span style={{ color: "var(--text-tertiary)" }}>SEARCH</span>
            </button>

            {/* KPI pills */}
            {metrics && (
              <div className="hdr-kpi" style={{ display: "flex", gap: 5 }}>
                {[
                  { label: "RET",    value: `${metrics.ret}%`,     color: "var(--positive)"  },
                  { label: "VOL",    value: `${metrics.vol}%`,     color: "var(--caution)"   },
                  { label: "SHARPE", value: metrics.sharpe,         color: parseFloat(metrics.sharpe) >= 1 ? "var(--positive)" : "var(--negative)" },
                  { label: "β",      value: metrics.beta,           color: "var(--text-primary)" },
                  { label: "DIV",    value: `${metrics.divYield}%`, color: "var(--pro)"       },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{
                    background: "var(--surface-2)",
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius-sm)",
                    padding: "3px 9px",
                    display: "flex", gap: 5, alignItems: "center",
                    flexShrink: 0,
                  }}>
                    <span style={{ color: "var(--text-tertiary)", fontFamily: "var(--font-mono)", fontSize: 8, letterSpacing: ".06em" }}>{label}</span>
                    <span style={{ color, fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: 10 }}>{value}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Weight badge */}
            <div style={{
              background: "var(--surface-2)",
              border: `1px solid ${weightColor}35`,
              borderRadius: "var(--radius-sm)",
              padding: "4px 10px",
              display: "flex", alignItems: "center", gap: 6,
              flexShrink: 0,
            }}>
              <span style={{ color: "var(--text-tertiary)", fontFamily: "var(--font-mono)", fontSize: 8, letterSpacing: ".06em" }}>WT</span>
              <span style={{ color: weightColor, fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: 11 }}>
                {totalWeight.toFixed(1)}%
              </span>
              {!weightOk && (
                <span style={{ color: weightColor, fontFamily: "var(--font-mono)", fontSize: 8 }}>{weightOver ? "OVER" : "UNDER"}</span>
              )}
            </div>

            {/* Divider */}
            <div style={{ width: 1, height: 22, background: "var(--border)", flexShrink: 0 }} />

            {/* User avatar dropdown */}
            {user && (
              <div ref={dropdownRef} style={{ position: "relative", flexShrink: 0 }}>
                <button
                  onClick={() => setDropdownOpen(v => !v)}
                  title="Account"
                  style={{
                    width: 30, height: 30, borderRadius: "50%",
                    background: "var(--accent-dim, rgba(0,255,157,0.12))",
                    border: "1px solid var(--accent-mid, var(--accent))",
                    color: "var(--accent)", cursor: "pointer",
                    fontFamily: "var(--font-mono)", fontSize: 10,
                    fontWeight: 700, letterSpacing: ".04em",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {initials}
                </button>
                {dropdownOpen && (
                  <div style={{
                    position: "absolute", top: 36, right: 0, zIndex: 9000,
                    background: "#0d1117", border: "1px solid #1c2333",
                    borderRadius: 6, minWidth: 200,
                    boxShadow: "0 8px 32px rgba(0,0,0,.7)",
                    overflow: "hidden",
                  }}>
                    {/* Header row: email + tier badge */}
                    <div style={{ padding: "12px 16px", borderBottom: "1px solid #1c2333" }}>
                      <div style={{ fontSize: 10, color: "#c9d1d9", fontFamily: "var(--font-mono)", marginBottom: 4, wordBreak: "break-all" }}>
                        {user.email}
                      </div>
                      <div style={{
                        display: "inline-block",
                        background: !isFree ? "rgba(255,184,0,0.12)" : "rgba(61,220,132,0.1)",
                        border: `1px solid ${!isFree ? "rgba(255,184,0,0.35)" : "rgba(61,220,132,0.3)"}`,
                        color: !isFree ? "#FFB800" : "#3ddc84",
                        borderRadius: 12, padding: "2px 8px",
                        fontSize: 8, letterSpacing: ".15em", fontFamily: "var(--font-mono)",
                      }}>
                        {(tier || "FREE").toUpperCase()}
                      </div>
                    </div>
                    {/* Menu items */}
                    {[
                      { label: "⚙  Settings", action: () => { onOpenSettings?.(); setDropdownOpen(false); } },
                      ...(isAdmin ? [{ label: "⬡  Admin", action: () => { setActiveTab("admin"); setDropdownOpen(false); } }] : []),
                    ].map(({ label, action }) => (
                      <button key={label} onClick={action} style={{
                        width: "100%", background: "transparent", border: "none",
                        borderBottom: "1px solid #1c2333", padding: "10px 16px",
                        textAlign: "left", cursor: "pointer",
                        fontFamily: "var(--font-mono)", fontSize: 10,
                        color: "#c9d1d9", letterSpacing: ".04em",
                        transition: "background .1s",
                      }}
                        onMouseEnter={e => { e.currentTarget.style.background = "#161b22"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                      >
                        {label}
                      </button>
                    ))}
                    <button
                      onClick={() => { onSignOut?.(); setDropdownOpen(false); }}
                      style={{
                        width: "100%", background: "transparent", border: "none",
                        padding: "10px 16px", textAlign: "left", cursor: "pointer",
                        fontFamily: "var(--font-mono)", fontSize: 10,
                        color: "#ff6b35", letterSpacing: ".04em",
                        transition: "background .1s",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = "#161b22"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                    >
                      → Sign out
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Billing / Upgrade button */}
            {onOpenBilling && (
              <button
                className="hdr-btn"
                onClick={onOpenBilling}
                style={{
                  background: tier && tier !== "free" ? "transparent" : "var(--accent-dim)",
                  border: `1px solid ${tier && tier !== "free" ? "var(--border)" : "var(--accent-mid, var(--accent))"}`,
                  color: tier && tier !== "free" ? "var(--text-secondary)" : "var(--accent)",
                  cursor: "pointer",
                  padding: "5px 12px",
                  display: "flex", alignItems: "center", gap: 6,
                  fontFamily: "var(--font-mono)",
                  fontSize: 9, letterSpacing: ".1em",
                  flexShrink: 0,
                  borderRadius: "var(--radius-sm)",
                }}
              >
                {tier && tier !== "free" ? "BILLING" : "UPGRADE"}
              </button>
            )}

            {/* Hamburger — mobile only */}
            <button
              onClick={() => setDrawerOpen(v => !v)}
              style={{
                display: "none",
                background: "transparent",
                border: "1px solid var(--border)",
                color: "var(--accent)",
                fontSize: 18,
                padding: "4px 10px",
                cursor: "pointer",
                borderRadius: "var(--radius-sm)",
                lineHeight: 1,
              }}
              className="hdr-hamburger"
              aria-label="Open navigation"
            >☰</button>

          </div>
        </div>

        {/* ── Nav bar — 6 dropdown groups ──────────────────────── */}
        <nav className="hdr-tab-bar" style={{
          padding: "0 24px",
          background: "var(--surface)",
          borderBottom: "1px solid var(--border)",
        }}>
          <div style={{
            maxWidth: 1320, margin: "0 auto",
            display: "flex", alignItems: "stretch",
            height: 36,
          }}>
            {NAV_GROUPS.map((group, gi) => {
              const isGroupActive = group.id === activeGroupId;
              const isOpen = openGroup === group.id;

              return (
                <div
                  key={group.id}
                  onMouseEnter={() => handleGroupEnter(group.id)}
                  onMouseLeave={handleGroupLeave}
                  style={{
                    position: "relative",
                    borderRight: gi < NAV_GROUPS.length - 1 ? "1px solid var(--border)" : "none",
                  }}
                >
                  <button
                    className={`nav-group-btn${isGroupActive || isOpen ? " active" : ""}`}
                    onClick={() => {
                      const first = group.tabs[0];
                      if (first) handleTabSelect(first.id);
                    }}
                    style={{
                      background: (isGroupActive || isOpen) ? "var(--accent-dim)" : "transparent",
                      border: "none",
                      borderBottom: `2px solid ${isGroupActive ? "var(--accent)" : "transparent"}`,
                      color: isGroupActive ? "var(--accent)" : "var(--text-secondary)",
                      padding: "0 16px",
                      height: "100%",
                      display: "flex", alignItems: "center", gap: 6,
                      fontFamily: "var(--font-mono)",
                      fontSize: 10,
                      letterSpacing: ".1em",
                      fontWeight: isGroupActive ? 700 : 400,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {group.label}
                    <span style={{ color: "var(--text-tertiary)", fontSize: 8 }}>▾</span>
                  </button>

                  {/* Dropdown */}
                  {isOpen && (
                    <div style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      minWidth: 210,
                      background: "var(--surface)",
                      border: "1px solid var(--border-strong)",
                      borderRadius: "0 0 var(--radius-lg) var(--radius-lg)",
                      boxShadow: "0 12px 32px rgba(0,0,0,0.5)",
                      zIndex: 200,
                      overflow: "hidden",
                    }}>
                      {group.tabs.map(tab => {
                        const locked = tab.pro && isFree;
                        const isActive = activeTab === tab.id;
                        return (
                          <button
                            key={tab.id}
                            className={`nav-dropdown-item${isActive ? " active" : ""}`}
                            onClick={() => { handleTabSelect(tab.id); setOpenGroup(null); }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              width: "100%",
                              padding: "9px 16px",
                              background: isActive ? "var(--accent-dim)" : "transparent",
                              border: "none",
                              borderLeft: `2px solid ${isActive ? "var(--accent)" : "transparent"}`,
                              color: locked ? "var(--text-tertiary)" : isActive ? "var(--accent)" : "var(--text-primary)",
                              cursor: "pointer",
                              opacity: locked ? 0.6 : 1,
                            }}
                          >
                            <span style={{
                              fontFamily: "var(--font-mono)",
                              fontSize: 9,
                              color: isActive ? "var(--accent)" : "var(--text-tertiary)",
                              background: "var(--surface-3)",
                              border: "1px solid var(--border)",
                              padding: "1px 5px",
                              borderRadius: "var(--radius-sm)",
                              letterSpacing: ".06em",
                              width: 42,
                              textAlign: "center",
                              flexShrink: 0,
                            }}>
                              {tab.code}
                            </span>
                            <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: ".06em", flex: 1 }}>
                              {tab.label}
                            </span>
                            {locked && (
                              <span style={{ fontSize: 10, color: "var(--text-tertiary)", flexShrink: 0 }}>🔒</span>
                            )}
                            {tab.pro && !isFree && (
                              <span style={{
                                fontFamily: "var(--font-mono)", fontSize: 7, letterSpacing: ".1em",
                                color: "var(--accent)", border: "1px solid rgba(61,220,132,.35)",
                                padding: "1px 4px", borderRadius: 2, flexShrink: 0,
                              }}>PRO</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>

      </header>

      {/* ── CMD+K search overlay ──────────────────────────────── */}
      {cmdkOpen && (
        <div className="cmdk-overlay" onClick={() => setCmdkOpen(false)}>
          <div className="cmdk-box" onClick={e => e.stopPropagation()}>
            <input
              ref={cmdkInputRef}
              className="cmdk-input"
              placeholder="Search modules... (↑↓ navigate, ↵ open)"
              value={cmdkQuery}
              onChange={e => { setCmdkQuery(e.target.value); setCmdkIdx(0); }}
              onKeyDown={handleCmdkKey}
            />
            <div className="cmdk-results">
              {cmdkResults.length === 0 && (
                <div style={{ padding: "16px 20px", color: "var(--text-tertiary)", fontFamily: "var(--font-mono)", fontSize: 12 }}>
                  No modules found
                </div>
              )}
              {cmdkResults.map((tab, i) => (
                <div
                  key={tab.id}
                  className={`cmdk-item${i === cmdkIdx ? " active" : ""}`}
                  onClick={() => { handleTabSelect(tab.id); setCmdkOpen(false); }}
                  onMouseEnter={() => setCmdkIdx(i)}
                >
                  <span className="cmdk-item-code">{tab.code}</span>
                  <span className="cmdk-item-label">{tab.label}</span>
                  <span className="cmdk-item-group">{tab.group}</span>
                </div>
              ))}
            </div>
            <div className="cmdk-footer">
              <span className="cmdk-key">↑↓</span>
              <span style={{ color: "var(--text-tertiary)", fontFamily: "var(--font-mono)", fontSize: 9 }}>navigate</span>
              <span className="cmdk-key">↵</span>
              <span style={{ color: "var(--text-tertiary)", fontFamily: "var(--font-mono)", fontSize: 9 }}>open</span>
              <span className="cmdk-key">esc</span>
              <span style={{ color: "var(--text-tertiary)", fontFamily: "var(--font-mono)", fontSize: 9 }}>close</span>
              <span style={{ marginLeft: "auto", color: "var(--text-tertiary)", fontFamily: "var(--font-mono)", fontSize: 9 }}>
                {cmdkResults.length} modules
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ── Mobile drawer backdrop ────────────────────────────── */}
      {drawerOpen && (
        <div
          onClick={() => setDrawerOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 199, display: "none" }}
          className="hdr-backdrop"
        />
      )}

      {/* ── Mobile drawer ─────────────────────────────────────── */}
      <div
        style={{
          position: "fixed", top: 0, left: 0,
          width: 270, height: "100vh",
          background: "var(--surface)",
          borderRight: "1px solid var(--border)",
          zIndex: 200,
          transform: drawerOpen ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 0.25s ease",
          overflowY: "auto",
          display: "none",
          flexDirection: "column",
          padding: "16px 0",
        }}
        className="hdr-drawer"
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 16px 16px", borderBottom: "1px solid var(--border)", marginBottom: 12 }}>
          <span style={{ color: "var(--accent)", fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: 14, letterSpacing: "0.1em" }}>NUVPORT OS</span>
          <button onClick={() => setDrawerOpen(false)} aria-label="Close navigation" style={{ background: "transparent", border: "none", color: "var(--text-secondary)", fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>
        {NAV_GROUPS.map(group => (
          <div key={group.id} style={{ marginBottom: 8 }}>
            <div style={{ color: "var(--text-tertiary)", fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.18em", padding: "4px 16px 8px", textTransform: "uppercase", fontWeight: 700 }}>{group.label}</div>
            {group.tabs.map(tab => {
              const locked = tab.pro && isFree;
              return (
                <button
                  key={tab.id}
                  onClick={() => { handleTabSelect(tab.id); setDrawerOpen(false); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left",
                    padding: "9px 16px",
                    background: activeTab === tab.id ? "var(--accent-dim)" : "transparent",
                    color: locked ? "var(--text-tertiary)" : activeTab === tab.id ? "var(--accent)" : "var(--text-primary)",
                    border: "none", borderLeft: `2px solid ${activeTab === tab.id ? "var(--accent)" : "transparent"}`,
                    fontFamily: "var(--font-mono)", fontSize: 12,
                    cursor: "pointer", letterSpacing: "0.04em",
                    opacity: locked ? 0.55 : 1,
                  }}
                >
                  <span style={{ color: "var(--text-tertiary)", fontSize: 9, width: 36 }}>{tab.code}</span>
                  <span style={{ flex: 1 }}>{tab.label}</span>
                  {locked && <span style={{ fontSize: 11 }}>🔒</span>}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* ── Upgrade prompt modal ──────────────────────────────── */}
      {upgradeOpen && (
        <div
          onClick={() => setUpgradeOpen(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 9100,
            background: "rgba(0,0,0,0.75)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "#0d1117",
              border: "1px solid rgba(61,220,132,.3)",
              borderRadius: 8,
              width: "min(400px, 90vw)",
              padding: "36px 32px",
              textAlign: "center",
              boxShadow: "0 0 40px rgba(61,220,132,.08), 0 24px 48px rgba(0,0,0,.7)",
            }}
          >
            <div style={{ fontSize: 28, marginBottom: 16 }}>🔒</div>
            <h3 style={{ fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 700, color: "var(--text-primary)", marginBottom: 10 }}>
              Pro Module
            </h3>
            <p style={{ fontFamily: "var(--font-body)", fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6, marginBottom: 24 }}>
              This module requires a Pro subscription. Upgrade to unlock all 16 modules, unlimited holdings, and live geo-risk intelligence.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button
                onClick={() => setUpgradeOpen(false)}
                style={{
                  background: "transparent", border: "1px solid var(--border)",
                  color: "var(--text-secondary)", padding: "9px 20px",
                  fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: ".08em",
                  cursor: "pointer", borderRadius: "var(--radius-md)",
                }}
              >
                CANCEL
              </button>
              <button
                onClick={() => { setUpgradeOpen(false); onOpenBilling?.(); }}
                style={{
                  background: "var(--accent)", border: "none",
                  color: "#0a0a0a", padding: "9px 24px",
                  fontFamily: "var(--font-mono)", fontSize: 10, fontWeight: 700, letterSpacing: ".1em",
                  cursor: "pointer", borderRadius: "var(--radius-md)",
                  boxShadow: "0 0 20px rgba(61,220,132,.3)",
                }}
              >
                UPGRADE TO PRO →
              </button>
            </div>
            <p style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--text-tertiary)", marginTop: 16, letterSpacing: ".06em" }}>
              $29/month · Cancel anytime
            </p>
          </div>
        </div>
      )}

      {/* ── Mobile bottom tab bar ─────────────────────────────── */}
      <div
        style={{
          position: "fixed", bottom: 0, left: 0, right: 0,
          height: 60,
          background: "var(--surface)",
          borderTop: "1px solid var(--border)",
          zIndex: 50,
          display: "none",
          alignItems: "center",
          justifyContent: "space-around",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
        className="hdr-bottom-bar"
      >
        {[
          { id: "dashboard",   label: "HOME",   icon: "⊡" },
          { id: "builder",     label: "BUILD",  icon: "◈" },
          { id: "valuation",   label: "VALUE",  icon: "◉" },
          { id: "world",       label: "GEO",    icon: "⊕" },
          { id: "marketwatch", label: "MKTS",   icon: "▤" },
        ].map(item => (
          <button
            key={item.id}
            onClick={() => handleTabSelect(item.id)}
            style={{
              background: "transparent",
              border: "none",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
              cursor: "pointer",
              padding: "8px 8px",
              borderTop: `2px solid ${activeTab === item.id ? "var(--accent)" : "transparent"}`,
              flex: 1,
            }}
          >
            <span style={{ color: activeTab === item.id ? "var(--accent)" : "var(--text-tertiary)", fontSize: 14 }}>{item.icon}</span>
            <span style={{ color: activeTab === item.id ? "var(--accent)" : "var(--text-tertiary)", fontFamily: "var(--font-mono)", fontSize: 8, letterSpacing: "0.08em" }}>{item.label}</span>
          </button>
        ))}
      </div>

    </>
  );
}
